import React, { useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { PiArrowSquareOut } from 'react-icons/pi';
import type {
  WebsiteTemplateRegistryTemplateSummary,
  WebsiteUpsertRequest,
} from 'src/api/generated';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classes } from 'src/modules/shared/utils/styles.util';
import type {
  WebsitePreviewDevice,
  WebsitePreviewTone,
} from '../../types/website-preview.types';
import { buildWebsiteTemplatePreviewUrl } from '../../utils/template-preview.util';
import { WebsiteTemplatePreviewContent } from './website-template-preview-content';

const MOCK_PREVIEW_DEBOUNCE_MS = 500;

/** Classes that change with the preview tone. */
const TONE_CLASSES: Record<
  WebsitePreviewTone,
  {
    root: string;
    header: string;
    body: string;
    phoneFrame: string;
    caption: string;
    note: string;
    link: string;
  }
> = {
  light: {
    root: 'overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm',
    header: 'justify-between border-b border-gray-200 px-4 py-3 sm:px-6',
    body: 'space-y-3 p-4 sm:p-6',
    phoneFrame: 'border-neutral-800',
    caption: 'text-neutral-600',
    note: 'text-neutral-900',
    link: 'text-neutral-700 underline hover:text-neutral-900',
  },
  dark: {
    root: 'space-y-3',
    header: 'justify-center',
    body: 'space-y-3',
    phoneFrame: 'border-neutral-600',
    caption: 'text-center text-neutral-300',
    note: 'text-center text-white',
    link: 'mx-auto text-neutral-200 hover:text-white',
  },
};

/** Props for the live site preview. */
interface WebsiteTemplateMockPreviewProps {
  /** Design shown in the preview. */
  template?: WebsiteTemplateRegistryTemplateSummary;
  /** Draft whose brand and contact values fill the preview. */
  form: WebsiteUpsertRequest;
  /**
   * Preview-session URL that shows the draft exactly as it would be
   * published, real listings included. Without it the design's mock preview
   * shows the draft's brand on sample listings.
   */
  sessionPreviewUrl?: string;
  /** Hostname shown in the browser bar, such as the site's address. */
  siteHost?: string;
  /** Device frame shown first. */
  defaultDevice?: WebsitePreviewDevice;
  /** Note under the preview, such as a reminder to publish. */
  note?: string;
  /** Color scheme: light on the page, dark inside the full-screen sheet. */
  tone?: WebsitePreviewTone;
}

/**
 * Shows the selected design with the agency's draft in a desktop or phone
 * frame: the draft's preview session when one is given, otherwise the design
 * package's mock preview with the draft's brand and sample listings.
 *
 * Draft changes reach the mock iframe after a short debounce without
 * remounting it. Designs without a mock preview get the descriptor-based
 * fallback.
 *
 * @param props - Design, draft, session URL, and presentation options.
 * @returns The preview panel with its device switch and caption.
 */
export const WebsiteTemplateMockPreview: React.FC<
  WebsiteTemplateMockPreviewProps
> = ({
  template,
  form,
  sessionPreviewUrl,
  siteHost,
  defaultDevice = 'desktop',
  note,
  tone = 'light',
}) => {
  const { t } = useI18n();
  const [device, setDevice] = useState<WebsitePreviewDevice>(defaultDevice);
  const debouncedForm = useDebounce(form, MOCK_PREVIEW_DEBOUNCE_MS);
  const showsSession = Boolean(template && sessionPreviewUrl);
  const iframeUrl = showsSession
    ? sessionPreviewUrl
    : buildWebsiteTemplatePreviewUrl({
        template,
        form: debouncedForm,
      });
  const toneClasses = TONE_CLASSES[tone];
  const content = (
    <WebsiteTemplatePreviewContent
      template={template}
      form={form}
      iframeUrl={iframeUrl}
      device={device}
    />
  );

  return (
    <section className={toneClasses.root}>
      <div
        className={classes(
          'flex flex-wrap items-center gap-3',
          toneClasses.header
        )}
      >
        {tone === 'light' ? (
          <Text as="h4" size="h6" weight="medium">
            {t('websites.templatePreview.title')}
          </Text>
        ) : null}
        <div className="w-48">
          <Tabs
            value={device}
            onChange={(value) =>
              setDevice(value === 'mobile' ? 'mobile' : 'desktop')
            }
            size="sm"
          >
            <Tab
              value="desktop"
              label={t('websites.templatePreview.device.desktop')}
            />
            <Tab
              value="mobile"
              label={t('websites.templatePreview.device.mobile')}
            />
          </Tabs>
        </div>
      </div>

      <div className={toneClasses.body}>
        {device === 'desktop' ? (
          <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
            <div className="flex items-center gap-2 border-b border-neutral-100 bg-neutral-50 px-3 py-2.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-amber-300" />
              <span className="h-2.5 w-2.5 rounded-full bg-success-300" />
              <span className="ml-2 truncate text-xs font-medium text-neutral-500">
                {siteHost}
              </span>
            </div>
            {content}
          </div>
        ) : (
          <div
            className={classes(
              'mx-auto w-full max-w-[320px] overflow-hidden rounded-[28px] border-[6px] bg-white',
              toneClasses.phoneFrame
            )}
          >
            {content}
          </div>
        )}

        {template ? (
          <Text size="small" className={toneClasses.caption}>
            {t(
              showsSession
                ? 'websites.templatePreview.captionLive'
                : 'websites.templatePreview.caption',
              { templateName: template.name }
            )}
          </Text>
        ) : null}
        {note ? (
          <Text size="small" weight="medium" className={toneClasses.note}>
            {note}
          </Text>
        ) : null}
        {iframeUrl ? (
          <a
            href={iframeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={classes(
              'inline-flex items-center gap-1 text-sm',
              toneClasses.link
            )}
          >
            {t('websites.templatePreview.openInNewTab')}
            <PiArrowSquareOut aria-hidden="true" className="h-4 w-4" />
          </a>
        ) : null}
      </div>
    </section>
  );
};
